import React from 'react';
import { motion } from 'framer-motion';
import { Mic, MessageSquare, Smartphone, Mail, PhoneCall, Globe } from 'lucide-react';
export function MistrBrain() {
  const inputs = [{
    icon: <Mic size={20} data-id="element-389" data-mp="1i05bbdlp7cad" />,
    label: 'Voice'
  }, {
    icon: <MessageSquare size={20} data-id="element-390" data-mp="1i1oupd2ni3gd" />,
    label: 'Slack'
  }, {
    icon: <Smartphone size={20} data-id="element-391" data-mp="1mwlzg7swdqng" />,
    label: 'SMS'
  }, {
    icon: <Mail size={20} data-id="element-392" data-mp="188ho5ow6chep" />,
    label: 'Email'
  }, {
    icon: <PhoneCall size={20} data-id="element-393" data-mp="1czzezk4hh1ly" />,
    label: 'WhatsApp'
  }, {
    icon: <Globe size={20} data-id="element-394" data-mp="1d2c6anw4sobo" />,
    label: 'Web'
  }];
  const outputs = ['Sales', 'Marketing', 'Operations', 'Support', 'Finance'];
  return <>
      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-395" data-mp="13apmi35q9gxb" />

      <section className="section-padding bg-section-gradient overflow-hidden relative" data-id="element-396" data-mp="pb6pa5egfpzs">
        {/* Subtle Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-teal/10 to-purple/10 rounded-full blur-[120px] opacity-40 pointer-events-none" data-id="element-397" data-mp="ubm2mwyqkxhr" />

        <div className="inner-container relative z-10" data-id="element-398" data-mp="38fxwex6g61h1">
          <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-399" data-mp="38empwplmafm8">
            <span className="text-teal text-sm font-poppins font-bold uppercase tracking-widest" data-id="element-400" data-mp="33gbkqs69otac">
              The Intelligence
            </span>
            <h2 className="font-poppins font-extrabold text-4xl md:text-5xl leading-tight mt-4 text-white" data-id="element-401" data-mp="2frikijim3z9x">
              Say It Once. MISTR Handles Everything.
            </h2>
          </div>

          <div className="relative w-full max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-12" data-id="element-402" data-mp="2pin11nnxrs6v">
            {/* Left: Inputs */}
            <div className="flex flex-col gap-4 w-full md:w-48 z-10" data-id="element-403" data-mp="1x096bwxwcmc0">
              {inputs.map((input, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg p-3 shadow-sm hover:border-teal/40 hover:shadow-md transition-all" data-id="element-404" data-mp="1s0mwzyl6vq8h" data-mpi={i}>
                  <div className="text-teal" data-id="element-405" data-mp="3e4emdxdimwdq">{input.icon}</div>
                  <span className="font-poppins font-medium text-sm text-white" data-id="element-406" data-mp="3j26dwqa90ghe">
                    {input.label}
                  </span>
                </motion.div>)}
            </div>

            {/* Center: Brain & Flow Animation */}
            <div className="relative w-64 h-64 flex-shrink-0 flex items-center justify-center z-10" data-id="element-407" data-mp="yfcx01le7jd5">
              {/* Animated flowing dots from left */}
              <div className="absolute left-[-100px] top-1/2 -translate-y-1/2 w-[100px] h-32 overflow-hidden hidden md:block" data-id="element-408" data-mp="2mvct97cd0u83">
                {[...Array(5)].map((_, i) => <motion.div key={`dot-in-${i}`} className="absolute w-2 h-2 bg-teal rounded-full shadow-[0_0_10px_rgba(7,133,183,0.8)]" initial={{
                x: -20,
                y: Math.random() * 100,
                opacity: 0
              }} animate={{
                x: 120,
                y: 64,
                opacity: [0, 1, 0]
              }} transition={{
                duration: 1.5,
                repeat: Infinity,
                delay: i * 0.3,
                ease: 'easeInOut'
              }} data-id="element-409" data-mp="2e3t339lsfsoq" data-mpi={i} />)}
              </div>

              {/* Brain Graphic */}
              <motion.div animate={{
              scale: [1, 1.05, 1]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="relative w-48 h-48" data-id="element-410" data-mp="2h9951cra4klp">
                <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-[0_0_40px_rgba(124,58,237,0.4)]" data-id="element-411" data-mp="2d6ce8yrl5i6o">
                  {/* Left Lobe (Teal) */}
                  <path d="M50 15 C30 15 15 30 15 50 C15 70 30 85 50 85 L50 15 Z" fill="#0785B7" opacity="0.8" data-id="element-412" data-mp="23b112kvtsj11" />
                  {/* Right Lobe (Purple) */}
                  <path d="M50 15 C70 15 85 30 85 50 C85 70 70 85 50 85 L50 15 Z" fill="#7c3aed" opacity="0.8" data-id="element-413" data-mp="1fkf3jnjg6h5a" />
                  {/* Center connection */}
                  <line x1="50" y1="20" x2="50" y2="80" stroke="white" strokeWidth="2" strokeDasharray="4 4" opacity="0.5" data-id="element-414" data-mp="10yhsmckdl1fo" />
                </svg>

                {/* Glowing effect */}
                <div className="absolute inset-0 bg-purple/30 blur-3xl rounded-full -z-10" data-id="element-415" data-mp="1jw13llqe8u4q"></div>
              </motion.div>

              {/* Animated flowing dots to right */}
              <div className="absolute right-[-100px] top-1/2 -translate-y-1/2 w-[100px] h-48 overflow-hidden hidden md:block" data-id="element-416" data-mp="1evdc6yy96b9z">
                {[...Array(10)].map((_, i) => <motion.div key={`dot-out-${i}`} className="absolute w-2 h-2 bg-purple rounded-full shadow-[0_0_10px_rgba(124,58,237,0.8)]" initial={{
                x: -20,
                y: 96,
                opacity: 0
              }} animate={{
                x: 120,
                y: Math.random() * 192,
                opacity: [0, 1, 0]
              }} transition={{
                duration: 1.2,
                repeat: Infinity,
                delay: i * 0.15,
                ease: 'easeOut'
              }} data-id="element-417" data-mp="1onsu5w9juzuv" data-mpi={i} />)}
              </div>
            </div>

            {/* Right: Outputs */}
            <div className="flex flex-col gap-4 w-full md:w-64 z-10" data-id="element-418" data-mp="2tfsp3vntfpyc">
              {outputs.map((output, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg p-4 shadow-sm hover:border-purple/40 hover:shadow-md transition-all" data-id="element-419" data-mp="33bvncqfjr4g0" data-mpi={i}>
                  <span className="font-poppins font-semibold text-sm text-white" data-id="element-420" data-mp="38iaofb2mv8e5">
                    {output.label}
                  </span>
                  <div className="text-purple" data-id="element-421" data-mp="50akq8441p6c">{output.icon}</div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
    </>;
}